import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useMotionValueEvent, useScroll, useTransform } from "framer-motion";
import { useLang } from "../Utility/Languagecontext";
import { SmartImg } from "../Utility/Smartimg";
import { PROJECT_IMGS } from "../Utility/Media";
import { featuredProjects, pick } from "../Data/Projectsdata";
import { formatAED } from "../Utility/format";

const EASE = [0.76, 0, 0.24, 1];
const DESKTOP = "(min-width: 961px)";

// سطر العنوان بيطلع من ورا قناع (المراقبة على العنوان الأب مش على المقصوص)
const MaskLine = ({ children, delay = 0, show }) => (
  <span className="mask">
    <motion.span
      className="mask__inner"
      initial={false}
      animate={{ y: show ? 0 : "115%" }}
      transition={{ duration: 1.1, delay: show ? delay : 0, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const isNum = (v) => typeof v === "number" && Number.isFinite(v);

export const MainProjects = () => {
  const { t, lang, isRTL, locale } = useLang();
  const l = lang;
  const items = useMemo(() => featuredProjects(), []);
  const n = items.length;

  // السعر بصيغة كل لغة (مثلاً AED 1.3M / 1,3 млн AED / ١٫٣ مليون د.إ.)
  const money = (v) =>
    isNum(v)
      ? formatAED(v, locale, lang)
      : t.projects.onRequest;
  const year = (y) => y.toLocaleString(locale, { useGrouping: false });
  const handover = (p) =>
    isNum(p.y) ? `${isNum(p.q) ? `${t.projects.quarters[p.q - 1]} ` : ""}${year(p.y)}` : p.status === "launch" ? t.projects.soon : "—";
  const pad = (v) => v.toLocaleString(locale, { minimumIntegerDigits: 2, useGrouping: false });

  const wrapRef = useRef(null);
  const trackRef = useRef(null);
  const headRef = useRef(null);
  // القياسات بتتخزن في ref عشان دوال الـ transform تقراها وقت السكرول
  const metrics = useRef({ shift: 0, desktop: true, dir: -1 });
  const [wrapHeight, setWrapHeight] = useState("auto");
  const [active, setActive] = useState(0);

  const headIn = useInView(headRef, { amount: 0.3 });

  // طول القسم = المسافة اللي الكروت هتتحركها + ارتفاع الشاشة (ديسكتوب بس)
  useLayoutEffect(() => {
    const measure = () => {
      const desktop = window.matchMedia(DESKTOP).matches;
      const track = trackRef.current;
      const shift = desktop && track ? Math.max(0, track.offsetWidth - window.innerWidth) : 0;
      metrics.current = { shift, desktop, dir: isRTL ? 1 : -1 };
      setWrapHeight(desktop ? shift + window.innerHeight : "auto");
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [isRTL, lang, n]);

  const { scrollYProgress } = useScroll({ target: wrapRef, offset: ["start start", "end end"] });

  // الكروت بتتحرك أفقيًا (وبتتعكس تلقائيًا في العربي)
  const x = useTransform(scrollYProgress, (p) => {
    const m = metrics.current;
    return m.desktop ? p * m.shift * m.dir : 0;
  });
  // الصورة جوه الكارت بتتحرك عكس الكارت بسرعة أقل = إحساس بالعمق
  const imgX = useTransform(scrollYProgress, (p) => {
    const m = metrics.current;
    return m.desktop ? `${(p - 0.5) * 12 * -m.dir}%` : "0%";
  });

  useMotionValueEvent(scrollYProgress, "change", (p) => {
    if (!metrics.current.desktop) return;
    setActive(Math.min(n - 1, Math.floor(p * n)));
  });

  return (
    <section className="projects" id="projects">
      <div className="projects__wrap" ref={wrapRef} style={{ height: wrapHeight }}>
        <div className="projects__stage">
          <div className="projects__head" ref={headRef}>
            <div>
              <p className="projects__label">{t.projects.label}</p>
              <h2 className="projects__title">
                {t.projects.title.map((line, i) => (
                  <MaskLine key={line} delay={i * 0.14} show={headIn}>
                    {line}
                  </MaskLine>
                ))}
              </h2>
            </div>
            <p className="projects__sub">{t.projects.sub}</p>
          </div>

          <motion.div className="projects__track" ref={trackRef} style={{ x }}>
            {items.map((p, i) => (
              <article key={p.id} className={`pcard ${i % 2 === 0 ? "pcard--wide" : "pcard--tall"}`}>
                <div className="pcard__media">
                  <SmartImg sources={PROJECT_IMGS[i % PROJECT_IMGS.length]} style={{ x: imgX }} />
                  <div className="pcard__shade" />
                </div>

                <div className="pcard__top">
                  <span className="pcard__no">{pad(i + 1)}</span>
                  <span className="pcard__type">{pick(p.type, l)}</span>
                </div>

                <div className="pcard__body">
                  <p className="pcard__area">{pick(p.area, l)}</p>
                  <h3 className="pcard__name">{pick(p.name, l)}</h3>
                  <dl className="pcard__meta">
                    <div>
                      <dt>{t.projects.from}</dt>
                      <dd>{money(p.price)}</dd>
                    </div>
                    <div>
                      <dt>{t.projects.handover}</dt>
                      <dd>{handover(p)}</dd>
                    </div>
                  </dl>
                  <Link to={`/project/${p.id}`} className="pcard__link">
                    {t.projects.view}
                  </Link>
                </div>
              </article>
            ))}

            <div className="pcard pcard--cta">
              <h3 className="pcard__name">{t.projects.cta.title}</h3>
              <p className="pcard__text">{t.projects.cta.text}</p>
              <a href="#contact" className="btn btn--solid">
                {t.projects.cta.button}
              </a>
            </div>
          </motion.div>

          <div className="projects__progress" aria-hidden="true">
            <span className="projects__count">
              {pad(active + 1)} / {pad(n)}
            </span>
            <span className="projects__bar">
              <motion.i style={{ scaleX: scrollYProgress, transformOrigin: isRTL ? "100% 50%" : "0% 50%" }} />
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};